/**
 * TrainingMode — the heart of the AI Responsibility Learning system.
 *
 * Locked product principles (Feb 13, 2026, operator brief):
 *   • One thing at a time. Show the learner's single current focus,
 *     never a library.
 *   • Hide unnecessary complexity. Reveal only the next correct step.
 *   • Calm. Clear. Almost magical.
 *
 * The page calls GET /api/training/next-step and renders one governed
 * next action. AI/system evidence is advisory; learner-owned competence
 * remains human-evidence gated.
 *
 * Everything else (the catalogue, the responsibility list, instructor
 * controls) is intentionally OFF this page. Those live on
 * /training/competencies and the instructor dashboard.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, Compass, Upload, Sparkles, ShieldCheck,
  Target, ChevronRight,
} from "lucide-react";

import ResponsibilityStageBadge from "../components/training/ResponsibilityStageBadge";
import ProofReflectionForm from "../components/training/ProofReflectionForm";
import ProofEvidencePanel from "../components/training/ProofEvidencePanel";
import ProofAllowanceChip from "../components/training/ProofAllowanceChip";
import LayerSplitPanel from "../components/training/LayerSplitPanel";
import LayerPromotionRail from "../components/training/LayerPromotionRail";
import ProofRequirementBlock from "../components/training/ProofRequirementBlock";
import RecentProofStatusCard from "../components/training/RecentProofStatusCard";
import LearningVideoReviews from "../components/learner/LearningVideoReviews";

const API = process.env.REACT_APP_BACKEND_URL;


export default function TrainingMode() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(null);
  const [reflectionOpen, setReflectionOpen] = useState(false);
  const [savedReflectionId, setSavedReflectionId] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [entitlement, setEntitlement] = useState(null);
  const [understandingBusy, setUnderstandingBusy] = useState(false);
  const [understandingFeedback, setUnderstandingFeedback] = useState("");
  const reviewIntent = searchParams.get("intent") === "mock_test" ? "mock_test" : "independent_drive";

  const loadStep = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/training/next-step`, {
        withCredentials: true,
      });
      setStep(r.data);
      if (r.data?.competency?.id) {
        try {
          const e = await axios.get(
            `${API}/api/training/proof/state/${r.data.competency.id}`,
            { withCredentials: true }
          );
          setEvidence(e.data?.evidence || []);
        } catch (_e) { /* non-fatal */ }
      }
      try {
        const ent = await axios.get(`${API}/api/proof-entitlements/me`,
          { withCredentials: true });
        setEntitlement(ent.data);
      } catch (_e) { /* non-fatal */ }
    } catch (_e) {
      toast.error("Couldn't load your training step. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStep(); }, [loadStep]);

  const onReflectionSaved = async (reflectionId) => {
    if (uploadBusy) return;
    setSavedReflectionId(reflectionId);
    setReflectionOpen(false);
    setUploadBusy(true);
    setUploadError("");
    try {
      const { data } = await axios.post(`${API}/api/training/proof/upload-context`, {
        competency_id: step.competency.id,
        responsibility_id: step.layers?.current_focus_id || step.next_responsibility?.id,
        reflection_id: reflectionId,
        review_intent: reviewIntent,
        ai_offer_id: searchParams.get("ai_offer_id") || null,
      }, { withCredentials: true });
      const query = new URLSearchParams({
        purpose: "lesson_evidence", label: "Your learning recording",
        learning_context_id: data.context.id,
      });
      nav(`/import-todays-recordings?${query}`);
    } catch (_error) {
      setUploadError("Your reflection is saved. We couldn't open your recording upload yet. Please retry.");
    } finally { setUploadBusy(false); }
  };

  const onUnderstandingSubmit = async (answer) => {
    if (!step?.competency?.id || !step?.next_responsibility?.id || understandingBusy) {
      return;
    }
    setUnderstandingBusy(true);
    setUnderstandingFeedback("");
    try {
      const { data } = await axios.post(
        `${API}/api/training/understanding-check`,
        {
          competency_id: step.competency.id,
          responsibility_id: step.next_responsibility.id,
          answer,
        },
        { withCredentials: true }
      );
      const advisoryStatus = data?.check?.advisory_status || "";
      const feedback = data?.check?.feedback || "";
      setUnderstandingFeedback(feedback);
      if (advisoryStatus === "ai_advisory_demonstrated") {
        toast.success(
          "Your explanation looks clear. This is advisory learning evidence — practical competence still needs instructor-observed passes."
        );
      } else {
        toast("Keep working on the understanding first.");
      }
      await loadStep();
    } catch (_e) {
      toast.error("Couldn't record that understanding check. Try again.");
    } finally {
      setUnderstandingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading your next step…</div>
      </div>
    );
  }

  // Graduation surface
  if (!step?.competency || step?.next_focus === "all_responsibilities_owned") {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 px-5 pb-24 pt-6">
        <TopBar onBack={() => nav(-1)} />
        <div className="max-w-md mx-auto mt-12 text-center">
          <div className="inline-flex p-5 rounded-full bg-emerald-100 mb-5">
            <ShieldCheck size={42} className="text-emerald-700" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 mb-3">
            Every Day-1 responsibility is currently learner-owned.
          </h1>
          <p className="text-slate-600 leading-relaxed">
            {step?.message || "New competencies coming soon."}
          </p>
        </div>
      </div>
    );
  }

  const focus = step.next_focus;
  const comp = step.competency;
  const state = step.state;
  const focusVerified = step.system_gate?.video?.recognised_moments
    ?? state.ai_advisory_proof_count
    ?? 0;
  const focusRequired = step.system_gate?.video?.required_moments
    ?? state.required_proof_count
    ?? 0;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 pb-32">
      <TopBar onBack={() => nav(-1)} />

      <div className="max-w-xl mx-auto px-5 pt-3">
        {/* Competency context strip */}
        <div className="flex items-center justify-between gap-3 mb-3"
             data-testid="training-competency-strip">
          <div className="flex items-center gap-2">
            <Compass size={18} className="text-slate-500" />
            <span className="text-sm font-medium text-slate-700"
                  data-testid="training-competency-label">
              {comp.label}
            </span>
          </div>
          <ResponsibilityStageBadge
            stage={state.stage}
            mode={state.mode}
          />
        </div>

        {/* Proof allowance chip — visible only on enforced plans
            (independent_proof). Invisible-tracking fast_track users
            never see it. */}
        {entitlement?.balance?.show_balance_to_user && (
          <div className="mb-4 flex justify-end">
            <ProofAllowanceChip balance={entitlement.balance} />
          </div>
        )}

        {/* Out-of-credits calm banner — locked operator tone:
            "Your upload is saved. Add proof credits to continue." */}
        {entitlement?.balance?.show_balance_to_user &&
         (entitlement.balance.total_balance || 0) <= 0 && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4"
               data-testid="training-out-of-credits-banner">
            <div className="text-sm font-semibold text-amber-900 mb-1">
              You've used your included proof analyses.
            </div>
            <div className="text-xs text-amber-800 leading-relaxed">
              Any new upload is saved. Add proof credits to continue
              analysis — your progress isn't lost.
            </div>
          </div>
        )}

        {/* Recent proof status — surfaces "Last upload: Awaiting Analysis
            / Awaiting Credits / Proof Verified / Building Proof / Not
            Yet Proven" so the learner can always see where their last
            upload stands. Renders nothing on a clean Training Mode. */}
        <div className="mb-4">
          <RecentProofStatusCard
            competencyId={comp.id}
          />
        </div>

        <LearningVideoReviews />

        {/* B0 Step 3 — calm-promotion rail. Shown only when the
            backend signals a layer-promotion moment (`transfer_next`
            focus). Owner-locked copy comes straight from the
            backend response — no client-side wording. */}
        {step.layer_promotion_note && (
          <div className="mb-4" data-testid="training-layer-promotion-rail-wrap">
            <LayerPromotionRail
              note={step.layer_promotion_note}
              variant="learner"
            />
          </div>
        )}

        {/* Focus card — ONE thing at a time */}
        <FocusCard
          focus={focus}
          comp={comp}
          state={state}
          layers={step.layers}
          nextResponsibility={step.next_responsibility}
          message={step.message}
          understandingQuestion={step.understanding_question}
          understandingFeedback={understandingFeedback}
          understandingBusy={understandingBusy}
          systemGate={step.system_gate}
          onOpenHub={() => nav(comp.hub_path)}
          onStartProof={() => setReflectionOpen(true)}
          onSubmitUnderstanding={onUnderstandingSubmit}
        />

        {/* Proof progress (always visible when learner has any state) */}
        <div className="mt-8">
          <ProofEvidencePanel
            verified={focusVerified}
            required={focusRequired}
            status={state.status}
            evidence={evidence}
          />
        </div>

        {/* Layer split — Current Focus / Retained Foundations /
            Instructor-led. Lets the learner SEE what the AI will
            and won't assess for this competency. Only shown when
            state has at least one responsibility transferred (or
            when there's instructor-led content to flag). */}
        {step.layers && (
          <div className="mt-6">
            <LayerSplitPanel layers={step.layers} />
          </div>
        )}

        {/* Mode toggle — calm, low-key. Only shown when learner has
            picked at least one responsibility (signals readiness to
            choose progression intensity). */}
        {step.governance?.phase === "instructor_supported"
          && state.owned_responsibilities?.length > 0 && (
          <ModeToggle
            currentMode={state.mode}
            competencyId={comp.id}
            onChanged={loadStep}
          />
        )}
      </div>

      {/* Pre-upload reflection */}
      {reflectionOpen && (
        <ProofReflectionForm
          competencyId={comp.id}
          competencyLabel={comp.label}
          onCancel={() => setReflectionOpen(false)}
          onSaved={onReflectionSaved}
        />
      )}

      {uploadBusy && <p role="status">Preparing your learning upload…</p>}
      {uploadError && <div role="alert" className="px-4 pb-4">
        <p>{uploadError}</p>
        <button type="button" disabled={uploadBusy} onClick={() => onReflectionSaved(savedReflectionId)}>Retry upload</button>
      </div>}

    </div>
  );
}

function TopBar({ onBack }) {
  return (
    <div className="max-w-xl mx-auto px-5 pt-5 flex items-center gap-3"
         data-testid="training-top-bar">
      <button
        onClick={onBack}
        className="p-2 rounded-full hover:bg-slate-200 active:scale-95 transition"
        data-testid="training-back"
      >
        <ArrowLeft size={20} className="text-slate-700" />
      </button>
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-emerald-600" />
        <span className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
          Training Mode
        </span>
      </div>
    </div>
  );
}

function FocusCard({
  focus,
  comp,
  state,
  layers,
  nextResponsibility,
  message,
  understandingQuestion,
  understandingFeedback,
  understandingBusy,
  systemGate,
  onOpenHub,
  onStartProof,
  onSubmitUnderstanding,
}) {
  const currentFocusLabel = layers?.current_focus?.[0]?.label
    || nextResponsibility?.label
    || comp.label;
  const retainedLabels = (layers?.retained_foundation || []).map((r) => r.label);
  const requirementBlock = focus === "prove_next" ? (
    <ProofRequirementBlock
      competencyLabel={comp.label}
      currentFocusLabel={currentFocusLabel}
      retainedLabels={retainedLabels}
      verifiedCount={systemGate?.video?.recognised_moments ?? state.ai_advisory_proof_count ?? 0}
      requiredCount={systemGate?.video?.required_moments ?? state.required_proof_count ?? 0}
    />
  ) : null;
  // 1. UNDERSTAND
  if (focus === "understand_first") {
    return (
      <Card
        accent="indigo"
        icon={<BookOpen size={26} />}
        stage="UNDERSTAND"
        title={nextResponsibility
          ? `Understand: ${nextResponsibility.label}`
          : `Start with ${comp.label}`}
        body={message}
        secondary={
          understandingQuestion ? (
            <UnderstandingCheckForm
              question={understandingQuestion}
              feedback={understandingFeedback}
              busy={understandingBusy}
              onSubmit={onSubmitUnderstanding}
            />
          ) : null
        }
        cta="Open the teaching"
        ctaIcon={<ChevronRight size={18} />}
        onCta={onOpenHub}
        testid="training-focus-understand"
      />
    );
  }
  // 2. USE THE TOOLS (transfer next responsibility)
  if (focus === "transfer_next") {
    const owned = state.owned_responsibilities?.length || 0;
    return (
      <Card
        accent="amber"
        icon={<Target size={26} />}
        stage="INSTRUCTOR REVIEW"
        title={nextResponsibility
          ? `Next responsibility: ${nextResponsibility.label}`
          : "Practise the next responsibility"}
        body={message}
        secondary={
          <>
            <div className="text-xs text-slate-500 mt-2"
                 data-testid="training-owned-summary">
              Responsibilities owned so far: {owned}
            </div>
            {requirementBlock}
          </>
        }
        cta="Open hub for reminders"
        ctaIcon={<BookOpen size={18} />}
        onCta={onOpenHub}
        secondaryCta="Add advisory video evidence"
        secondaryCtaIcon={<Upload size={18} />}
        onSecondaryCta={onStartProof}
        testid="training-focus-transfer"
      />
    );
  }
  // 3. PROVE IT
  if (focus === "prove_next") {
    return (
      <Card
        accent="emerald"
        icon={<Upload size={26} />}
        stage="PROVE IT"
        title="Upload your next proof drive"
        body={message}
        secondary={requirementBlock}
        cta="Start proof upload"
        ctaIcon={<ChevronRight size={18} />}
        onCta={onStartProof}
        secondaryCta="Refresh on the understanding"
        secondaryCtaIcon={<BookOpen size={18} />}
        onSecondaryCta={onOpenHub}
        testid="training-focus-prove"
      />
    );
  }
  // 4. PRE-INSTRUCTOR EVIDENCE READY — still provisional.
  if (focus === "awaiting_instructor") {
    return (
      <Card
        accent="slate"
        icon={<ShieldCheck size={26} />}
        stage="PROVISIONAL EVIDENCE"
        title="Your evidence is saved"
        body={message}
        secondary={
          <div className="mt-4 rounded-xl border border-slate-200 bg-white/70 p-3 text-sm text-slate-700">
            Keep practising. NDG can help you learn and preserve evidence, but
            it does not mark driving competence as learner-owned on its own.
          </div>
        }
        cta="Review the teaching"
        ctaIcon={<BookOpen size={18} />}
        onCta={onOpenHub}
        secondaryCta="Add more video evidence"
        secondaryCtaIcon={<Upload size={18} />}
        onSecondaryCta={onStartProof}
        testid="training-focus-awaiting-instructor"
      />
    );
  }

  // 5. HUMAN INDEPENDENT-PASS GATE
  if (focus === "instructor_confirm") {
    return (
      <Card
        accent="amber"
        icon={<Target size={26} />}
        stage="INDEPENDENT PASSES"
        title="Instructor observation still needed"
        body={message}
        secondary={
          <div className="mt-4 rounded-xl border border-amber-200 bg-white/70 p-3 text-sm text-slate-700">
            System and AI evidence can support the decision, but learner-owned
            competence needs two distinct instructor-observed independent
            passes, unless your instructor records an explicit exception.
          </div>
        }
        cta="Review the teaching"
        ctaIcon={<BookOpen size={18} />}
        onCta={onOpenHub}
        testid="training-focus-instructor-confirm"
      />
    );
  }

  // Fallback (shouldn't happen)
  return (
    <Card
      accent="slate"
      icon={<Compass size={26} />}
      stage="TRAINING"
      title={comp.label}
      body={message}
      cta="Open hub"
      onCta={onOpenHub}
      testid="training-focus-fallback"
    />
  );
}

function UnderstandingCheckForm({ question, feedback, busy, onSubmit }) {
  const [answer, setAnswer] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const value = answer.trim();
    if (!value || busy) return;
    onSubmit?.(value);
  };

  return (
    <form
      onSubmit={submit}
      className="mt-5 rounded-2xl border border-indigo-200 bg-white/80 p-4"
      data-testid="training-understanding-check"
    >
      <div className="text-xs font-bold tracking-[0.12em] text-indigo-700 mb-2">
        ONE UNDERSTANDING CHECK
      </div>
      <p className="text-sm font-semibold text-slate-900 leading-relaxed">
        {question}
      </p>
      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={4}
        maxLength={1500}
        placeholder="Explain it in your own words…"
        className="mt-3 w-full resize-none rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-900 outline-none focus:border-indigo-500"
        data-testid="training-understanding-answer"
      />
      {feedback && (
        <p className="mt-2 text-sm text-slate-600" data-testid="training-understanding-feedback">
          {feedback}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || !answer.trim()}
        className="mt-3 w-full rounded-xl bg-indigo-600 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="training-understanding-submit"
      >
        {busy ? "Checking…" : "Check my understanding"}
      </button>
    </form>
  );
}


const ACCENTS = {
  indigo:  { bg: "bg-indigo-50",  border: "border-indigo-200",  icon: "bg-indigo-100 text-indigo-700",   text: "text-indigo-900",  btn: "bg-indigo-600 hover:bg-indigo-700" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   icon: "bg-amber-100 text-amber-700",     text: "text-amber-900",   btn: "bg-amber-600 hover:bg-amber-700" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "bg-emerald-100 text-emerald-700", text: "text-emerald-900", btn: "bg-emerald-600 hover:bg-emerald-700" },
  slate:   { bg: "bg-slate-50",   border: "border-slate-200",   icon: "bg-slate-100 text-slate-700",     text: "text-slate-900",   btn: "bg-slate-700 hover:bg-slate-800" },
};

function Card({ accent, icon, stage, title, body, secondary, cta, ctaIcon,
                onCta, secondaryCta, secondaryCtaIcon, onSecondaryCta, testid }) {
  const c = ACCENTS[accent] || ACCENTS.slate;
  return (
    <div
      className={`rounded-3xl border-2 ${c.border} ${c.bg} p-6 shadow-sm`}
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-2xl ${c.icon}`}>{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold tracking-[0.15em] text-slate-500 mb-1">
            {stage}
          </div>
          <h2 className={`text-2xl font-semibold ${c.text} leading-tight`}
              data-testid={`${testid}-title`}>
            {title}
          </h2>
          <p className="text-slate-700 leading-relaxed mt-3"
             data-testid={`${testid}-body`}>
            {body}
          </p>
          {secondary}
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-2">
        <button
          onClick={onCta}
          className={`${c.btn} text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition active:scale-[0.98]`}
          data-testid={`${testid}-cta`}
        >
          {cta}{ctaIcon}
        </button>
        {secondaryCta && (
          <button
            onClick={onSecondaryCta}
            className="text-slate-700 font-medium py-3 rounded-xl hover:bg-white/60 flex items-center justify-center gap-2 transition"
            data-testid={`${testid}-secondary-cta`}
          >
            {secondaryCtaIcon}{secondaryCta}
          </button>
        )}
      </div>
    </div>
  );
}

function ModeToggle({ currentMode, competencyId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const switchMode = async (mode) => {
    if (mode === currentMode || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/training/set-mode`,
        { competency_id: competencyId, mode },
        { withCredentials: true }
      );
      toast.success(
        mode === "independent"
          ? "Switched to Independent evidence mode (8 AI/video evidence moments for coaching)"
          : "Switched to Instructor-guided evidence mode (4 AI/video evidence moments for coaching)"
      );
      onChanged?.();
    } catch (_e) {
      toast.error("Couldn't switch mode. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5"
         data-testid="training-mode-toggle">
      <div className="text-xs font-bold tracking-[0.12em] text-slate-500 mb-2">
        PROGRESSION MODE
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => switchMode("instructor_guided")}
          disabled={busy}
          className={`p-3 rounded-xl text-sm font-semibold transition ${
            currentMode === "instructor_guided"
              ? "bg-amber-600 text-white"
              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}
          data-testid="training-mode-guided"
        >
          Instructor-guided<br />
          <span className="text-xs font-normal opacity-80">~4 advisory evidence moments</span>
        </button>
        <button
          onClick={() => switchMode("independent")}
          disabled={busy}
          className={`p-3 rounded-xl text-sm font-semibold transition ${
            currentMode === "independent"
              ? "bg-emerald-600 text-white"
              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}
          data-testid="training-mode-independent"
        >
          Independent evidence<br />
          <span className="text-xs font-normal opacity-80">~8 advisory evidence moments</span>
        </button>
      </div>
    </div>
  );
}

